import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { usePetContext } from '../context/PetContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dog,
  Cat,
  Bird,
  Fish,
  Turtle,
  Rabbit,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Heart,
  Calendar,
  Scale,
  Palette,
  Users,
  Upload,
  X,
  Image as ImageIcon,
  ChevronLeft,
  ChevronRight,
  Star,
  Eye,
  EyeOff,
  Link,
  UserPlus,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import PetSwitcher from '../components/PetSwitcher';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const petIcons = {
  dog: Dog,
  cat: Cat,
  bird: Bird,
  fish: Fish,
  reptile: Turtle,
  small_pet: Rabbit,
  horse: Dog,
  other: Dog
};

const MyPets = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { petType, config, PET_CONFIGS, petSingular } = usePetContext();
  const isApproved = user && (user.is_beta_user || user.is_charter_member || user.is_admin || user.is_sysadmin || user.is_operator_admin);
  const PET_LIMIT = 5;
  
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  // Feature Pet Dialog state
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
  const [featurePet, setFeaturePet] = useState(null);
  const [featureImage, setFeatureImage] = useState('');
  const [featurePrivacy, setFeaturePrivacy] = useState('pet_name');
  const [featureCaption, setFeatureCaption] = useState('');
  const [featureSubmitting, setFeatureSubmitting] = useState(false);
  const [myFeaturedSubmissions, setMyFeaturedSubmissions] = useState([]);
  
  const [newPet, setNewPet] = useState({
    name: '',
    pet_type: petType,
    custom_pet_type: '',  // For "other" pet types
    breed: '',
    species: '',
    age_years: '',
    age_months: '',
    gender: '',
    weight: '',
    weight_unit: 'lbs',
    color: '',
    bio: '',
    image_url: '',
    gallery_images: [],
    temperament: '',
    favorite_activities: []
  });
  const [activitiesInput, setActivitiesInput] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Image upload state
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPets();
    fetchMyFeaturedSubmissions();
  }, []);
  
  const fetchMyFeaturedSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/featured-pets/my-submissions`);
      setMyFeaturedSubmissions(response.data);
    } catch (error) {
      console.error('Error fetching featured submissions:', error);
    }
  };

  const fetchPets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/pets/me`);
      setPets(response.data);
    } catch (error) {
      console.error('Error fetching pets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle image file selection and upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (uploadedImages.length + files.length > 10) {
      toast.error('Maximum 10 images allowed per pet');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    
    try {
      const response = await axios.post(`${API}/uploads/pet-images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const newImages = response.data.uploaded.map(img => img.url);
      setUploadedImages(prev => [...prev, ...newImages]);
      
      // Set first image as primary if none set
      if (!newPet.image_url && newImages.length > 0) {
        setNewPet(prev => ({ 
          ...prev, 
          image_url: newImages[0],
          gallery_images: [...(prev.gallery_images || []), ...newImages.slice(1)]
        }));
      } else {
        setNewPet(prev => ({ 
          ...prev, 
          gallery_images: [...(prev.gallery_images || []), ...newImages]
        }));
      }
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.count} images uploaded, ${response.data.errors.length} failed`);
      } else {
        toast.success(`${response.data.count} image(s) uploaded!`);
      }
    } catch (error) {
      toast.error('Failed to upload images');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Remove an uploaded image
  const removeImage = (imageUrl) => {
    setUploadedImages(prev => prev.filter(url => url !== imageUrl));
    
    if (newPet.image_url === imageUrl) {
      // If removing primary image, set next gallery image as primary
      const remainingImages = uploadedImages.filter(url => url !== imageUrl);
      if (remainingImages.length > 0) {
        setNewPet(prev => ({
          ...prev,
          image_url: remainingImages[0],
          gallery_images: remainingImages.slice(1)
        }));
      } else {
        setNewPet(prev => ({ ...prev, image_url: '', gallery_images: [] }));
      }
    } else {
      setNewPet(prev => ({
        ...prev,
        gallery_images: (prev.gallery_images || []).filter(url => url !== imageUrl)
      }));
    }
  };

  // Set an image as the primary photo
  const setPrimaryImage = (imageUrl) => {
    const allImages = uploadedImages;
    const otherImages = allImages.filter(url => url !== imageUrl);
    setNewPet(prev => ({
      ...prev,
      image_url: imageUrl,
      gallery_images: otherImages
    }));
    toast.success('Primary photo updated');
  };

  const handleCreatePet = async () => {
    if (!newPet.name) {
      toast.error('Please enter a name');
      return;
    }
    
    setSaving(true);
    try {
      const petData = {
        ...newPet,
        age_years: newPet.age_years ? parseInt(newPet.age_years) : null,
        age_months: newPet.age_months ? parseInt(newPet.age_months) : null,
        weight: newPet.weight ? parseFloat(newPet.weight) : null,
        favorite_activities: activitiesInput.split(',').map(s => s.trim()).filter(Boolean)
      };
      
      await axios.post(`${API}/pets`, petData);
      toast.success(`${petData.name} added!`);
      setAddDialogOpen(false);
      resetForm();
      fetchPets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add pet');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePet = async () => {
    if (!selectedPet) return;
    
    setSaving(true);
    try {
      const petData = {
        ...newPet,
        age_years: newPet.age_years ? parseInt(newPet.age_years) : null,
        age_months: newPet.age_months ? parseInt(newPet.age_months) : null,
        weight: newPet.weight ? parseFloat(newPet.weight) : null,
        favorite_activities: activitiesInput.split(',').map(s => s.trim()).filter(Boolean)
      };
      
      await axios.put(`${API}/pets/${selectedPet.id}`, petData);
      toast.success('Updated!');
      setEditDialogOpen(false);
      fetchPets();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePet = async (petId, petName) => {
    if (!window.confirm(`Are you sure you want to remove ${petName}?`)) return;
    
    try {
      await axios.delete(`${API}/pets/${petId}`);
      toast.success(`${petName} removed`);
      fetchPets();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEditDialog = (pet) => {
    setSelectedPet(pet);
    const allImages = [pet.image_url, ...(pet.gallery_images || [])].filter(Boolean);
    setUploadedImages(allImages);
    setNewPet({
      name: pet.name,
      pet_type: pet.pet_type,
      breed: pet.breed || '',
      species: pet.species || '',
      age_years: pet.age_years?.toString() || '',
      age_months: pet.age_months?.toString() || '',
      gender: pet.gender || '',
      weight: pet.weight?.toString() || '',
      weight_unit: pet.weight_unit || 'lbs',
      color: pet.color || '',
      bio: pet.bio || '',
      image_url: pet.image_url || '',
      gallery_images: pet.gallery_images || [],
      temperament: pet.temperament || '',
      favorite_activities: pet.favorite_activities || []
    });
    setActivitiesInput((pet.favorite_activities || []).join(', '));
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setNewPet({
      name: '',
      pet_type: petType,
      custom_pet_type: '',
      breed: '',
      species: '',
      age_years: '',
      age_months: '',
      gender: '',
      weight: '',
      weight_unit: 'lbs',
      color: '',
      bio: '',
      image_url: '',
      gallery_images: [],
      temperament: '',
      favorite_activities: []
    });
    setActivitiesInput('');
    setSelectedPet(null);
    setUploadedImages([]);
  };

  // Feature Pet handlers
  const openFeatureDialog = (pet) => {
    setFeaturePet(pet);
    const allImages = [pet.image_url, ...(pet.gallery_images || [])].filter(Boolean);
    setFeatureImage(allImages[0] || '');
    setFeaturePrivacy('pet_name');
    setFeatureCaption('');
    setFeatureDialogOpen(true);
  };

  const handleFeatureSubmit = async () => {
    if (!featurePet || !featureImage) {
      toast.error('Please select a photo');
      return;
    }
    
    setFeatureSubmitting(true);
    try {
      await axios.post(`${API}/featured-pets`, {
        pet_id: featurePet.id,
        image_url: featureImage,
        privacy_level: featurePrivacy,
        caption: featureCaption || null
      });
      
      toast.success('Photo submitted for featuring!');
      setFeatureDialogOpen(false);
      fetchMyFeaturedSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit photo');
    } finally {
      setFeatureSubmitting(false);
    }
  };

  const handleDeleteFeatured = async (submissionId) => {
    if (!window.confirm('Remove this photo from the homepage rotation?')) return;
    
    try {
      await axios.delete(`${API}/featured-pets/${submissionId}`);
      toast.success('Photo removed from rotation');
      fetchMyFeaturedSubmissions();
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  // Check if a pet can be featured (max 2 per pet, 4 per member)
  const canFeaturePet = (petId) => {
    const petSubmissions = myFeaturedSubmissions.filter(
      s => s.pet_id === petId && ['pending', 'approved'].includes(s.status)
    );
    const totalActive = myFeaturedSubmissions.filter(
      s => ['pending', 'approved'].includes(s.status)
    );
    return petSubmissions.length < 2 && totalActive.length < 4;
  };

  const getPetFeaturedCount = (petId) => {
    return myFeaturedSubmissions.filter(
      s => s.pet_id === petId && ['pending', 'approved'].includes(s.status)
    ).length;
  };

  // Group pets by type
  const petsByType = pets.reduce((acc, pet) => {
    const type = pet.pet_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(pet);
    return acc;
  }, {});

  const filteredPets = activeTab === 'all' 
    ? pets 
    : pets.filter(p => p.pet_type === activeTab);

  const PetIcon = ({ type }) => {
    const Icon = petIcons[type] || Dog;
    return <Icon className="w-5 h-5" />;
  };

  const renderPetForm = (isEdit = false) => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Input
          placeholder="Name *"
          value={newPet.name}
          onChange={(e) => setNewPet({ ...newPet, name: e.target.value })}
        />
        <Select
          value={newPet.pet_type}
          onValueChange={(val) => setNewPet({ ...newPet, pet_type: val, custom_pet_type: '' })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Pet Type" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PET_CONFIGS).map(([type, cfg]) => (
              <SelectItem key={type} value={type}>
                <div className="flex items-center gap-2">
                  <PetIcon type={type} />
                  {cfg.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Custom pet type field for "other" */}
      {newPet.pet_type === 'other' && (
        <Input
          placeholder="What type of pet? (e.g., rabbit, hamster, turtle...)"
          value={newPet.custom_pet_type}
          onChange={(e) => setNewPet({ ...newPet, custom_pet_type: e.target.value })}
        />
      )}
      
      <div className="grid grid-cols-2 gap-4">
        <Input
          placeholder={newPet.pet_type === 'dog' || newPet.pet_type === 'cat' ? 'Breed' : 'Species'}
          value={newPet.pet_type === 'dog' || newPet.pet_type === 'cat' ? newPet.breed : newPet.species}
          onChange={(e) => {
            if (newPet.pet_type === 'dog' || newPet.pet_type === 'cat') {
              setNewPet({ ...newPet, breed: e.target.value });
            } else {
              setNewPet({ ...newPet, species: e.target.value });
            }
          }}
        />
        <Select
          value={newPet.gender}
          onValueChange={(val) => setNewPet({ ...newPet, gender: val })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Gender" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="male">Male</SelectItem>
            <SelectItem value="female">Female</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid grid-cols-4 gap-4">
        <Input
          placeholder="Age (years)"
          type="number"
          value={newPet.age_years}
          onChange={(e) => setNewPet({ ...newPet, age_years: e.target.value })}
        />
        <Input
          placeholder="Age (months)"
          type="number"
          value={newPet.age_months}
          onChange={(e) => setNewPet({ ...newPet, age_months: e.target.value })}
        />
        <Input
          placeholder="Weight"
          type="number"
          value={newPet.weight}
          onChange={(e) => setNewPet({ ...newPet, weight: e.target.value })}
        />
        <Select
          value={newPet.weight_unit}
          onValueChange={(val) => setNewPet({ ...newPet, weight_unit: val })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="lbs">lbs</SelectItem>
            <SelectItem value="kg">kg</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <Input
        placeholder="Color/Markings"
        value={newPet.color}
        onChange={(e) => setNewPet({ ...newPet, color: e.target.value })}
      />
      
      {/* Image Upload Section */}
      <div className="space-y-3">
        <label className="text-sm font-medium block">Pet Photos</label>
        
        {/* Upload Button */}
        <div className="flex items-center gap-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/jpeg,image/png,image/gif,image/webp"
            multiple
            className="hidden"
            data-testid="pet-image-upload-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || uploadedImages.length >= 10}
            data-testid="pet-image-upload-btn"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {uploading ? 'Uploading...' : 'Upload Photos'}
          </Button>
          <span className="text-xs text-muted-foreground">
            {uploadedImages.length}/10 photos (max 5MB each)
          </span>
        </div>
        
        {/* Image Preview Grid */}
        {uploadedImages.length > 0 && (
          <div className="grid grid-cols-4 gap-2">
            {uploadedImages.map((imageUrl, index) => (
              <div
                key={imageUrl}
                className={`relative aspect-square rounded-lg overflow-hidden border-2 ${
                  newPet.image_url === imageUrl ? 'border-primary ring-2 ring-primary/30' : 'border-transparent'
                }`}
              >
                <img
                  src={imageUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}` : imageUrl}
                  alt={`Pet photo ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {/* Primary badge */}
                {newPet.image_url === imageUrl && (
                  <Badge className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 bg-primary">
                    Primary
                  </Badge>
                )}
                {/* Action buttons */}
                <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  {newPet.image_url !== imageUrl && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="h-7 text-xs"
                      onClick={() => setPrimaryImage(imageUrl)}
                    >
                      Set Primary
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="h-7 w-7 p-0"
                    onClick={() => removeImage(imageUrl)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Empty state */}
        {uploadedImages.length === 0 && (
          <div 
            className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <ImageIcon className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              Click to upload photos of your pet
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              JPEG, PNG, GIF, or WebP
            </p>
          </div>
        )}
      </div>
      
      <Textarea
        placeholder="About your pet..."
        value={newPet.bio}
        onChange={(e) => setNewPet({ ...newPet, bio: e.target.value })}
        rows={3}
      />
      
      <Input
        placeholder="Temperament (e.g., friendly, shy, playful)"
        value={newPet.temperament}
        onChange={(e) => setNewPet({ ...newPet, temperament: e.target.value })}
      />
      
      <Input
        placeholder="Favorite activities (comma separated)"
        value={activitiesInput}
        onChange={(e) => setActivitiesInput(e.target.value)}
      />
      
      <Button 
        className="w-full btn-primary" 
        onClick={isEdit ? handleUpdatePet : handleCreatePet}
        disabled={saving}
      >
        {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
        {isEdit ? 'Update' : 'Add'} Pet
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-medium flex items-center gap-3">
              <Heart className="w-8 h-8 text-primary" />
              My Pets
            </h1>
            <p className="text-muted-foreground">
              Manage all your furry, feathery, and scaly friends
            </p>
          </div>
          <div className="flex gap-3">
            <PetSwitcher />
            {!isApproved && pets.length >= PET_LIMIT ? (
              <Button className="btn-primary" disabled data-testid="add-pet-btn">
                <Plus className="w-4 h-4 mr-2" />
                Limit Reached ({PET_LIMIT})
              </Button>
            ) : (
            <Dialog open={addDialogOpen} onOpenChange={(open) => {
              setAddDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-pet-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Pet
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Add a New Pet</DialogTitle>
                </DialogHeader>
                {renderPetForm(false)}
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {/* Pet limit banner for unapproved users */}
        {!isApproved && (
          <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm flex items-center gap-2" data-testid="pet-limit-banner">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>Free accounts can register up to {PET_LIMIT} pets ({pets.length}/{PET_LIMIT} used). Contact an admin for unlimited access.</span>
          </div>
        )}

        {/* Pet Type Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex-wrap">
            <TabsTrigger value="all" className="flex items-center gap-2">
              All Pets
              <Badge variant="secondary">{pets.length}</Badge>
            </TabsTrigger>
            {Object.entries(petsByType).map(([type, typePets]) => {
              const cfg = PET_CONFIGS[type];
              if (!cfg) return null;
              return (
                <TabsTrigger key={type} value={type} className="flex items-center gap-2">
                  <PetIcon type={type} />
                  {cfg.name}
                  <Badge variant="secondary">{typePets.length}</Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value={activeTab}>
            {filteredPets.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <Heart className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl mb-2">
                    {activeTab === 'all' ? 'No pets yet' : `No ${PET_CONFIGS[activeTab]?.name.toLowerCase() || 'pets'} yet`}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Add your first pet to get started!
                  </p>
                  <Button onClick={() => setAddDialogOpen(true)} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Pet
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPets.map((pet) => {
                  const PIcon = petIcons[pet.pet_type] || Dog;
                  const petConfig = PET_CONFIGS[pet.pet_type];
                  const allImages = [pet.image_url, ...(pet.gallery_images || [])].filter(Boolean);
                  
                  return (
                    <PetCard 
                      key={pet.id} 
                      pet={pet} 
                      PIcon={PIcon} 
                      petConfig={petConfig} 
                      allImages={allImages}
                      onEdit={() => openEditDialog(pet)}
                      onDelete={() => handleDeletePet(pet.id, pet.name)}
                      onFeature={() => openFeatureDialog(pet)}
                      canFeature={canFeaturePet(pet.id)}
                      featuredCount={getPetFeaturedCount(pet.id)}
                    />
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit {selectedPet?.name}</DialogTitle>
          </DialogHeader>
          {renderPetForm(true)}
        </DialogContent>
      </Dialog>

      {/* Feature Pet Dialog */}
      <Dialog open={featureDialogOpen} onOpenChange={setFeatureDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Feature {featurePet?.name} on Homepage
            </DialogTitle>
            <DialogDescription>
              Your pet's photo will appear in the rotating hero section on the homepage. Choose how much info to share.
            </DialogDescription>
          </DialogHeader>
          
          {featurePet && (
            <div className="space-y-4">
              {/* Photo Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block">Select Photo</label>
                <div className="grid grid-cols-4 gap-2">
                  {[featurePet.image_url, ...(featurePet.gallery_images || [])].filter(Boolean).map((img) => (
                    <button
                      key={img}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        featureImage === img 
                          ? 'border-primary ring-2 ring-primary/30' 
                          : 'border-transparent hover:border-muted-foreground/30'
                      }`}
                      onClick={() => setFeatureImage(img)}
                    >
                      <img
                        src={img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                        alt="Pet photo"
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Privacy Level */}
              <div>
                <label className="text-sm font-medium mb-2 block">Privacy Level</label>
                <Select value={featurePrivacy} onValueChange={setFeaturePrivacy}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anonymous">
                      <div className="flex items-center gap-2">
                        <EyeOff className="w-4 h-4" />
                        <span>Anonymous - Just the photo</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="pet_name">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        <span>Pet Name - Show {featurePet?.name}'s name</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="link_profile">
                      <div className="flex items-center gap-2">
                        <Link className="w-4 h-4" />
                        <span>Link Profile - Link to your profile</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="friend_request">
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4" />
                        <span>Friend Request - Allow friend requests</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Caption */}
              <div>
                <label className="text-sm font-medium mb-2 block">Caption (optional)</label>
                <Input
                  placeholder="Say something about this photo..."
                  value={featureCaption}
                  onChange={(e) => setFeatureCaption(e.target.value.slice(0, 100))}
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {featureCaption.length}/100 characters
                </p>
              </div>

              {/* Info */}
              <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                <p className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  Trusted members (30+ days, no flags) get auto-approved!
                </p>
              </div>

              {/* Submit Button */}
              <Button 
                className="w-full btn-primary"
                onClick={handleFeatureSubmit}
                disabled={!featureImage || featureSubmitting}
              >
                {featureSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Star className="w-4 h-4 mr-2" />
                    Feature This Photo
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Pet Card Component with Image Carousel
const PetCard = ({ pet, PIcon, petConfig, allImages, onEdit, onDelete, onFeature, canFeature, featuredCount }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };
  
  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };
  
  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };
  
  return (
    <Card className="card-interactive overflow-hidden" data-testid={`pet-card-${pet.id}`}>
      {/* Pet Image Carousel */}
      <div className="aspect-square bg-muted relative group">
        {allImages.length > 0 ? (
          <>
            <img 
              src={getImageUrl(allImages[currentImageIndex])} 
              alt={pet.name}
              className="w-full h-full object-cover"
            />
            {/* Navigation arrows for multiple images */}
            {allImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 h-8 w-8"
                  onClick={prevImage}
                  data-testid={`pet-prev-image-${pet.id}`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 h-8 w-8"
                  onClick={nextImage}
                  data-testid={`pet-next-image-${pet.id}`}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
                {/* Image dots indicator */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {allImages.map((img, idx) => (
                    <button
                      key={`dot-${img}-${idx}`}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                      onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                    />
                  ))}
                </div>
                {/* Image count badge */}
                <Badge className="absolute bottom-2 right-2 bg-black/60 text-white text-xs">
                  {currentImageIndex + 1}/{allImages.length}
                </Badge>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <PIcon className="w-20 h-20 text-muted-foreground/30" />
          </div>
        )}
        <Badge 
          className="absolute top-3 right-3"
          style={{ backgroundColor: petConfig?.primaryColor }}
        >
          {petConfig?.singular || 'Pet'}
        </Badge>
        
        {/* Featured badge if pet has featured photos */}
        {featuredCount > 0 && (
          <Badge className="absolute top-3 left-3 bg-amber-500 text-white">
            <Star className="w-3 h-3 mr-1 fill-current" />
            Featured ({featuredCount})
          </Badge>
        )}
      </div>
      
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-heading text-xl font-medium">{pet.name}</h3>
            <p className="text-sm text-muted-foreground">
              {pet.breed || pet.species || petConfig?.singular}
            </p>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`edit-pet-${pet.id}`}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="text-destructive hover:text-destructive"
              data-testid={`delete-pet-${pet.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Feature My Pet Button */}
        {allImages.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="w-full mb-3 border-amber-500/50 text-amber-600 hover:bg-amber-50 hover:text-amber-700"
            onClick={onFeature}
            disabled={!canFeature}
            data-testid={`feature-pet-${pet.id}`}
          >
            <Star className="w-4 h-4 mr-2" />
            {canFeature ? 'Feature on Homepage' : `Max featured (${featuredCount}/2)`}
          </Button>
        )}
        
        {/* Pet Details */}
        <div className="space-y-2 text-sm">
          {(pet.age_years || pet.age_months) && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4" />
              {pet.age_years ? `${pet.age_years} years` : ''} 
              {pet.age_months ? ` ${pet.age_months} months` : ''}
            </div>
          )}
          {pet.weight && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Scale className="w-4 h-4" />
              {pet.weight} {pet.weight_unit}
            </div>
          )}
          {pet.color && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Palette className="w-4 h-4" />
              {pet.color}
            </div>
          )}
          {pet.friends_count > 0 && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="w-4 h-4" />
              {pet.friends_count} friends
            </div>
          )}
        </div>
        
        {pet.bio && (
          <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
            {pet.bio}
          </p>
        )}
        
        {pet.temperament && (
          <Badge variant="outline" className="mt-3">
            {pet.temperament}
          </Badge>
        )}
      </CardContent>
    </Card>
  );
};

export default MyPets;
